import React, { useCallback, useMemo, useState } from 'react';
import { Cross2Icon, DrawingPinIcon } from '@radix-ui/react-icons';
import { Spinner } from '../../../../components';
import { useKanbanStore } from '../../../../store/kanbanStore';
import { getLabelChipStyle, normalizeLabelName, pickNewLabelColor } from '../../../../utils/taskLabel';
import { SidebarField } from './SidebarField';

interface TaskLabelsFieldProps {
  labelIds: number[];
  onChange: (labelIds: number[]) => void;
  disabled?: boolean;
}

export const TaskLabelsField: React.FC<TaskLabelsFieldProps> = ({
  labelIds,
  onChange,
  disabled = false,
}) => {
  const { taskLabels, labelPalette, createTaskLabel } = useKanbanStore();
  const [newLabelName, setNewLabelName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const assignedLabels = useMemo(
    () =>
      labelIds
        .map((id) => taskLabels.find((l) => l.labelId === id))
        .filter((l): l is NonNullable<typeof l> => Boolean(l)),
    [labelIds, taskLabels]
  );

  const unassignedLabels = useMemo(
    () => taskLabels.filter((l) => !labelIds.includes(l.labelId)),
    [labelIds, taskLabels]
  );

  const addLabelId = useCallback(
    (id: number) => {
      if (labelIds.includes(id)) return;
      onChange([...labelIds, id]);
    },
    [labelIds, onChange]
  );

  const removeLabelId = useCallback(
    (id: number) => {
      onChange(labelIds.filter((labelId) => labelId !== id));
    },
    [labelIds, onChange]
  );

  const handleAddLabel = useCallback(async () => {
    const name = normalizeLabelName(newLabelName);
    if (!name || isCreating) return;

    setCreateError(null);
    const existing = taskLabels.find(
      (l) => l.name.toLowerCase() === name.toLowerCase()
    );

    if (existing) {
      addLabelId(existing.labelId);
      setNewLabelName('');
      return;
    }

    setIsCreating(true);
    try {
      const colorHex = pickNewLabelColor(
        labelPalette.length ? labelPalette : undefined
      );
      const created = await createTaskLabel(name, colorHex);
      addLabelId(created.labelId);
      setNewLabelName('');
    } catch {
      setCreateError('Failed to create label. Try again.');
    } finally {
      setIsCreating(false);
    }
  }, [
    addLabelId,
    createTaskLabel,
    isCreating,
    labelPalette,
    newLabelName,
    taskLabels,
  ]);

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        void handleAddLabel();
      }
    },
    [handleAddLabel]
  );

  return (
    <SidebarField label="Labels" icon={<DrawingPinIcon aria-hidden="true" />}>
      <div className="task-details-modal__labels">
        {assignedLabels.length > 0 && (
          <div className="task-details-modal__tags">
            {assignedLabels.map((label) => (
              <span
                key={label.labelId}
                className="task-details-modal__tag task-details-modal__tag--assigned"
                style={getLabelChipStyle(label.colorHex)}
              >
                {label.name}
                <button
                  type="button"
                  aria-label={`Remove ${label.name}`}
                  onClick={() => removeLabelId(label.labelId)}
                  disabled={disabled}
                >
                  <Cross2Icon />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="task-details-modal__tag-input">
          <input
            type="text"
            placeholder="Add label…"
            value={newLabelName}
            onChange={(e) => {
              setCreateError(null);
              setNewLabelName(e.target.value);
            }}
            onKeyDown={handleInputKeyDown}
            onBlur={() => {
              if (newLabelName.trim()) void handleAddLabel();
            }}
            disabled={isCreating || disabled}
            maxLength={100}
          />
          {isCreating && <Spinner size="small" />}
        </div>

        {createError && (
          <p className="task-details-modal__label-error" role="alert">
            {createError}
          </p>
        )}

        {unassignedLabels.length > 0 && (
          <div className="task-details-modal__label-suggestions">
            {unassignedLabels.map((label) => (
              <button
                key={label.labelId}
                type="button"
                className="task-details-modal__label-suggestion"
                style={getLabelChipStyle(label.colorHex)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addLabelId(label.labelId)}
              >
                + {label.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </SidebarField>
  );
};
